// Modelo del listado de "Partidos abiertos" (Falta un jugador): opciones de los filtros nuevos,
// armado de los params de GET /api/app/open-matches y formateo de lo que muestra la tarjeta.
// Sin React ni red: se testea directo y lo comparten la página de canchas y OpenMatchCard.
import dayjs from "dayjs";
import { ratingRangesParam } from "../reservas/components/reviewsModel";

/** Tipos de partido que acepta el back (`kind`), en el orden en que se muestran. */
export const MATCH_KINDS = ["open", "mixed", "male", "female"];

export const MATCH_KIND_LABELS = {
	open: "Abierto",
	mixed: "Mixto",
	male: "Masculino",
	female: "Femenino",
};

/** Opciones de "Cantidad de jugadores" (lo que falta): los tokens son los del param `missing` del back. */
export const MISSING_OPTIONS = [
	{ value: "1", label: "Falta 1" },
	{ value: "2", label: "Faltan 2" },
	{ value: "3plus", label: "Faltan 3 o más" },
];

export const MATCHES_PAGE_SIZE = 20;

/** Etiqueta de cuántos jugadores faltan en un partido concreto ("Falta 1", "Faltan 3"). */
export const missingLabel = (missing) =>
	Number(missing) === 1 ? "Falta 1" : `Faltan ${Number(missing) || 0}`;

/**
 * Traduce los filtros de la página al query del back. Solo manda lo que tiene valor, igual que el
 * listado de clubes (`date` llega vacío hasta que se toca "Buscar partidos").
 * @param {object} f  estado de filtros de la página
 * @param {number} page  página (desde 1)
 */
export const buildOpenMatchesParams = (f, page = 1) => {
	const params = { page, limit: MATCHES_PAGE_SIZE };
	if (f.stateId) params.stateid = f.stateId;
	if (f.cityId) params.cityid = f.cityId;
	if (f.sport) params.sport = f.sport;
	if (f.date) params.date = f.date;
	if (f.time) params.time = f.time;
	if (f.surfaces?.length) params.surface = f.surfaces.join(",");
	if (f.durations?.length) params.durationMinutes = f.durations.join(",");
	if (f.services?.length) params.services = f.services.join(",");
	if (f.enclosures?.length) params.enclosure = f.enclosures.join(",");
	if (f.ratingRanges?.length) params.ratingRanges = ratingRangesParam(f.ratingRanges);
	if (f.kinds?.length) params.kind = f.kinds.join(",");
	if (f.missing?.length) params.missing = f.missing.join(",");
	return params;
};

/** Respuesta del listado -> { matches, page, hasMore, total } tolerante a formas inesperadas. */
export const normalizeOpenMatches = (response) => {
	const info = response?.data?.info || {};
	return {
		matches: Array.isArray(info.matches) ? info.matches : [],
		page: Number(info.page) || 1,
		total: Number(info.total) || 0,
		hasMore: Boolean(info.hasMore),
	};
};

/** "mar 10 nov" del día del partido (`date` viene como YYYY-MM-DD o ISO). */
export const formatMatchDay = (date) => {
	const d = dayjs(date);
	return d.isValid() ? d.format("ddd D MMM") : "";
};

/** "Cierra 10/11 13:00 hs" a partir de `closeAt`; vacío si no hay fecha válida. */
export const formatCloseAt = (closeAt) => {
	const d = dayjs(closeAt);
	return d.isValid() ? `Cierra ${d.format("DD/MM HH:mm")} hs` : "";
};

export const formatPricePerPlayer = (amount) =>
	`$${(Number(amount) || 0).toLocaleString("es-AR")}`;

/** Iniciales para el avatar de un jugador ("Lucia Perez" -> "LP"). */
export const playerInitials = (name) => {
	const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
	if (!parts.length) return "?";
	return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
};

/* ------------------------- Detalle y "Sumarme" (T9) ------------------------- */

export const MATCH_STATE_LABELS = {
	open: "Abierto",
	confirmed: "Partido completo",
	cancelled: "Partido cancelado",
	kept: "Se mantuvo la cancha",
	closed: "Inscripción cerrada",
};

/**
 * Estado a mostrar. El back solo guarda open|confirmed|cancelled|kept: "closed" se deriva en el
 * front cuando el partido sigue `open` pero ya pasó `closeAt` (el back responde 409 al sumarse).
 */
export const matchViewState = (match, now = new Date()) => {
	const status = match?.status || "open";
	if (status !== "open") return status;
	const close = dayjs(match?.closeAt);
	return close.isValid() && close.valueOf() <= now.valueOf() ? "closed" : "open";
};

/** Mensaje en español para un error de POST /join (409: lleno, cerrado, ya anotado, no abierto). */
export const joinErrorMessage = (err) => {
	const status = err?.response?.status;
	const message = String(err?.response?.data?.message || "");
	if (status === 409) {
		if (/anotad|already/i.test(message)) return "Ya estás anotado en este partido.";
		if (/llen|full/i.test(message)) return "El partido está lleno. Mirá otros partidos abiertos.";
		if (/cerr|closed/i.test(message)) return "La inscripción de este partido ya cerró.";
		return "Este partido ya no está abierto para sumarse.";
	}
	return message || "No pudimos sumarte al partido. Probá de nuevo.";
};

/**
 * Body de POST /join con los mismos campos que el paso de datos de la reserva de cancha: un solo
 * `name` (lastname vacío), teléfono con el prefijo del país elegido y `marketingOptIn` solo si se tildó.
 */
export const buildJoinPayload = (values, country, marketingOptIn) => {
	const prefix = country?.prefix || "+54";
	const digits = prefix.replace(/\D/g, "");
	const raw = String(values.phone || "").trim().replace(new RegExp(`^(\\+?${digits}\\s*)`), "");
	const payload = {
		name: values.name,
		lastname: "",
		email: values.email,
		phone: raw ? `${prefix} ${raw}` : "",
		dni: values.dni,
	};
	if (marketingOptIn) payload.marketingOptIn = true;
	return payload;
};

const origin = () => (typeof window !== "undefined" ? window.location.origin : "");

/** Link de gestión del jugador (lo usa T11): `/partidos/<id>?token=<manageToken>`. */
export const manageUrl = (matchId, token, base = origin()) =>
	`${base}/partidos/${matchId}?token=${token}`;

/** Link público para compartir el partido. */
export const shareUrl = (shareToken, base = origin()) => `${base}/partidos/compartir/${shareToken}`;

const MANAGE_KEY = (matchId) => `openMatch:manage:${matchId}`;

/** Guarda el manageToken en este navegador para volver a "Mi partido" (T11 lo lee con readManageToken). */
export const saveManageToken = (matchId, token) => {
	try {
		if (matchId && token) localStorage.setItem(MANAGE_KEY(matchId), token);
	} catch (_) {}
};

/** Borra el token guardado (se bajó del partido o el servidor lo rechazó). */
export const clearManageToken = (matchId) => {
	try {
		if (matchId) localStorage.removeItem(MANAGE_KEY(matchId));
	} catch (_) {}
};

/**
 * Borra todos los tokens de gestión guardados en este navegador (`openMatch:manage:*`).
 * Se llama al cerrar sesión: el token del creador no debe sobrevivir a la sesión, porque daría
 * "Cancelar partido" a cualquiera que use el mismo navegador. Solo toca esas claves.
 */
export const clearAllManageTokens = () => {
	try {
		const prefix = MANAGE_KEY("");
		const keys = [];
		for (let i = 0; i < localStorage.length; i += 1) {
			const k = localStorage.key(i);
			if (k && k.startsWith(prefix)) keys.push(k);
		}
		keys.forEach((k) => localStorage.removeItem(k));
	} catch (_) {}
};

export const readManageToken = (matchId) => {
	try {
		return localStorage.getItem(MANAGE_KEY(matchId)) || "";
	} catch (_) {
		return "";
	}
};

/* ------------------------- Crear partido abierto (T10) ------------------------- */

/** "Quién lo puede ver": público (aparece en Partidos abiertos) o solo con el link. */
export const MATCH_VISIBILITIES = [
	{ value: "public", label: "Público", hint: "Aparece en Partidos abiertos" },
	{ value: "link", label: "Solo con link", hint: "Solo lo ven quienes reciban el link" },
];

/** "Si al cierre faltan jugadores": cancelar y liberar la cancha, o mantener la reserva y dividir. */
export const IF_NOT_FULL_OPTIONS = [
	{ value: "cancel", label: "Cancelar y liberar la cancha" },
	{ value: "keep", label: "Mantener la reserva y dividir entre los anotados" },
];

/**
 * Body de POST /api/app/open-matches: los campos de datos salen de `buildJoinPayload` (mismo teléfono con
 * prefijo, `lastname` vacío y `marketingOptIn` solo si se tildó) más el turno elegido y las reglas del partido.
 * @param {object} rules  turno + reglas: clubId, courtId, date, slotStart, durationMinutes, sport,
 *                        alreadyPlayers, kind, visibility, ifNotFull
 */
export const buildCreatePayload = (rules, values, country, marketingOptIn) => ({
	clubId: rules.clubId,
	courtId: rules.courtId,
	date: rules.date,
	slotStart: rules.slotStart,
	durationMinutes: rules.durationMinutes,
	...(rules.sport ? { sport: rules.sport } : {}),
	alreadyPlayers: rules.alreadyPlayers,
	kind: rules.kind,
	visibility: rules.visibility,
	ifNotFull: rules.ifNotFull,
	...buildJoinPayload(values, country, marketingOptIn),
});

/**
 * Clasifica un error de POST /open-matches para que la pantalla decida a dónde volver:
 * `auth` (401 sin sesión: ir a /login), `slot` (409 turno tomado/bloqueado: volver a Horario), `closed` (409 el cierre ya pasó: elegir otro
 * horario), `disabled` (403 el club apagó la función) u `other` (mensaje del back o genérico).
 * @returns {{kind: "auth"|"slot"|"closed"|"disabled"|"other", message: string}}
 */
export const classifyCreateError = (err) => {
	const status = err?.response?.status;
	const message = String(err?.response?.data?.message || "");
	// Creating needs a session (T16): the caller sends the user to /login and comes back.
	if (status === 401) return { kind: "auth", message: "Iniciá sesión para publicar el partido." };
	if (status === 409) {
		if (/cierre|closeAt|close/i.test(message)) {
			return { kind: "closed", message: "Ese horario ya está muy cerca para abrir un partido. Elegí otro horario." };
		}
		return { kind: "slot", message: "Ese turno se ocupó. Elegí otro horario." };
	}
	if (status === 403) {
		return { kind: "disabled", message: "Este club no tiene los partidos abiertos habilitados." };
	}
	return { kind: "other", message: message || "No pudimos publicar el partido. Probá de nuevo." };
};

/** Texto para compartir por WhatsApp (el link de compartir lo arma `shareUrl`). */
export const buildShareMessage = ({ clubName, date, slotStart, missing, link }) =>
	`${missingLabel(missing)} para un partido en ${clubName} el ${formatMatchDay(date)} ${dayjs(date).format("DD/MM")} a las ${slotStart} hs. ¿Te sumás? ${link}`;

/** "Cierra 10/11 16:00 hs": inicio del turno menos `closeHours`; vacío si falta la fecha u hora. */
export const closeAtPreview = (date, slotStart, closeHours) => {
	const start = dayjs(`${date} ${slotStart}`, "YYYY-MM-DD HH:mm", true);
	if (!date || !slotStart || !start.isValid()) return "";
	return formatCloseAt(start.subtract(Number(closeHours) || 0, "hour").toISOString());
};

/** `?crear=partido` en la ficha del club: la reserva se convierte en la creación de un partido abierto. */
export const wantsOpenMatchCreate = (search) => new URLSearchParams(search || "").get("crear") === "partido";

/** Link de entrada al flujo de creación de un club (lo usan "Crear partido abierto" y el estado vacío). */
export const createOpenMatchPath = (clubId) => `/canchas/${clubId}?crear=partido`;

/* ------------------------- Vista del jugador / bajarse (T11) ------------------------- */

/**
 * Motivo (`me.leaveBlockedReason` del back) por el que el jugador no puede bajarse, en castellano.
 * `isCreator` cambia el texto de `creator_cannot_leave` / `not_open` / `closed`: el creador no se baja, cancela.
 */
export const leaveBlockedMessage = (reason, { isCreator = false } = {}) => {
	if (isCreator) return "Como creaste el partido no podés bajarte. Si necesitás cancelarlo ahora, hablá con el club.";
	switch (reason) {
		case "closed":
			return "Ya cerró la inscripción: no podés bajarte desde acá. Si no podés ir, avisale al club.";
		case "not_open":
			return "Este partido ya no está abierto: no podés bajarte desde acá.";
		case "not_a_player":
			return "Ya no estás anotado en este partido.";
		default:
			return "No podés bajarte de este partido desde acá.";
	}
};

/** Mensaje para un error de /leave o /cancel: usa el del back en 409 y uno genérico si no hay respuesta. */
export const manageActionErrorMessage = (err, fallback) => {
	const status = err?.response?.status;
	const message = String(err?.response?.data?.message || "");
	if (status === 401 || status === 403) return "Tu link de gestión ya no es válido para esta acción.";
	return message || fallback;
};

/** El token del link no sirve (inválido, de otro partido o de alguien que ya no juega). */
export const isManageTokenRejected = (err) => [401, 403].includes(err?.response?.status);
